import { useEffect, useState } from 'react'
import useHttp from '@/shared/hooks/useHttp'

import {
  StyledPaginatorWrapper,
  StyledPageButton,
  StyledDots
} from './styled-components'

const DEFAULT_PER_PAGE = 20
const MAX_PAGES_DISPLAY = 10

const useFeedbackList = (listUrl, initialFilters = {}) => {
  const { sendRequest, isLoading, getConfig } = useHttp()
  const [feedbacks, setFeedbacks] = useState([])
  const [total, setTotal] = useState(0)
  const [perPage] = useState(initialFilters.perPage || DEFAULT_PER_PAGE)
  const [page, setPage] = useState(1)
  const [error, setError] = useState(null)

  const [filters, setFilters] = useState({
    search: '',
    start: '',
    end: '',
    version: 'legacy',
    feedbackType: 'all',
    ...initialFilters,
  })

  const [searchInput, setSearchInput] = useState(filters.search)

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const buildQuery = () =>
    Object.entries({ ...filters, page, perPage })
      .filter(([, v]) => v !== '' && v !== undefined && v !== null)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&')

  useEffect(() => {
    setError(null)
    const query = buildQuery()
    sendRequest(
      `${listUrl}?${query}`,
      getConfig('GET'),
      resp => {
        setFeedbacks(resp.data || [])
        setTotal(resp.total || 0)
      }
    )
  }, [listUrl, filters, page, perPage])

  useEffect(() => {
    const timeout = setTimeout(() => {
      updateFilter('search', searchInput)
    }, 500)
    return () => clearTimeout(timeout)
  }, [searchInput])

  const Paginator = () => {
    const lastPage = Math.ceil(total / perPage)
    if (lastPage <= 1) return null

    const pages = []
    pages.push(1)

    let start = Math.max(2, page - 2)
    let end = Math.min(lastPage - 1, page + 2)

    if (page <= 3) end = Math.min(lastPage - 1, MAX_PAGES_DISPLAY)
    if (page >= lastPage - 2) start = Math.max(2, lastPage - MAX_PAGES_DISPLAY + 1)

    if (start > 2) pages.push('...')
    for (let i = start; i <= end; i++) pages.push(i)
    if (end < lastPage - 1) pages.push('...')
    if (lastPage > 1) pages.push(lastPage)

    return (
      <StyledPaginatorWrapper>
        {pages.map((p, idx) =>
          p === '...' ? (
            <StyledDots key={idx}>…</StyledDots>
          ) : (
            <StyledPageButton
              key={p}
              onClick={() => setPage(p)}
              active={p === page}
              disabled={p === page}
            >
              {p}
            </StyledPageButton>
          )
        )}
      </StyledPaginatorWrapper>
    )
  }

  return {
    searchInput, setSearchInput,
    filters,
    setVersion: v => updateFilter('version', v),
    setFeedbackType: t => updateFilter('feedbackType', t),
    setStartDate: d => updateFilter('start', d),
    setEndDate: d => updateFilter('end', d),
    goToPage: setPage,
    feedbackType: filters.feedbackType,
    feedbackVersion: filters.version,
    feedbacks, isLoading, error, Paginator
  }
}

export default useFeedbackList
